'use client';

import { ModalId } from '@heidi/schema/ml-scribe';

import { useQueuedModal } from '@/hooks/useQueuedModal';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { PatientListIntroductionModalLoading } from '@heidi/patients';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { track } from '@/lib/analytics/track';

/**
 * Card body is lazily imported so its hero imagery only loads once the queue
 * surfaces the card (Radix mounts `PopoverContent` on open), per the
 * modal-queue lazy-shell pattern.
 */
const PatientListIntroductionModalContent = dynamic(
  () =>
    import('./patient-list-introduction-modal-content').then(
      (mod) => mod.PatientListIntroductionModalContent
    ),
  { loading: () => <PatientListIntroductionModalLoading />, ssr: false }
);

/**
 * One-time Patients GA awareness card.
 *
 * Visibility is owned by the modal queue (`ModalId.PATIENT_LIST_INTRODUCTION`):
 * eligibility (`release-scribe-enable-patient-list` on) is computed in
 * `GlobalModalsWrapper` / `launchModalsConfigAtom`, and `seen_modals` keeps it
 * to once per user across devices. Dismissal stamps `first_seen_at`, which the
 * companion {@link PatientListNavSpotlight} reads to surface itself two days
 * later.
 *
 * Rendered as a non-modal corner card pinned to the bottom-left of the
 * viewport (same shell as {@link TrialEndDiscreetCard}) so it nudges toward the
 * Patients sidebar item without blocking the app. Design: Figma node
 * 2833:167881.
 */
export const PatientListIntroductionModal = () => {
  const { isOpen, handleClose, dismissible } = useQueuedModal(
    ModalId.PATIENT_LIST_INTRODUCTION
  );
  const router = useRouter();
  const intl = useIntl();
  const viewTrackedRef = useRef(false);

  // Fire the view event at most once. The queue can re-surface this modal after
  // it yields to a higher-priority modal, so guard against inflating the funnel.
  useEffect(() => {
    if (isOpen && !viewTrackedRef.current) {
      viewTrackedRef.current = true;
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.ModalViewed,
        props: { screen_name: ScreenName.PatientListIntroductionModal },
      });
    }
  }, [isOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Escape key closes; outside clicks are prevented below so the card only
      // dismisses via its own affordances. Respect `dismissible` from the queue.
      if (!open && dismissible) handleClose();
    },
    [handleClose, dismissible]
  );

  const handleViewPatients = useCallback(() => {
    void router.push('/patients');
    handleClose();
  }, [router, handleClose]);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      <PopoverAnchor
        aria-hidden
        className="pointer-events-none fixed bottom-4 start-4 z-[55] size-px"
      />
      <PopoverContent
        side="top"
        align="start"
        sideOffset={0}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        aria-label={intl.formatMessage({
          defaultMessage: 'A home for your patients',
          id: 'S2NCpQ/cr2',
        })}
        data-testid="patient-list-intro-modal"
        className={cn(
          'w-full max-w-64 overflow-hidden rounded-xl border-0 bg-surface-container p-0 shadow-sheet ring-0',
          // The experimental design-system variant adds `ring-1 ring-border` to
          // PopoverContent — strip it so the card reads as borderless.
          'data-[experimental=true]:border-0 data-[experimental=true]:ring-0',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
        )}
      >
        <PatientListIntroductionModalContent
          onPrimary={handleViewPatients}
          onDismiss={handleClose}
        />
      </PopoverContent>
    </Popover>
  );
};
